import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import { Card, CardContent } from "../components/ui/card";
import * as Icons from "lucide-react";

export const B2BRemarketing = () => {
  const [refServices, isServicesVisible] = useScrollReveal({ once: true });
  const [refMengen, isMengenVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section - Apple Style */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Großmengen & IT-Remarketing
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Professionelle Remarketing-Lösungen für IT-Hardware in großen Stückzahlen
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/request-quote">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Remarketing anfragen
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Services - Apple Style */}
      <section ref={refServices} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isServicesVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Remarketing-Services für Unternehmen
          </motion.h2>
          
          <motion.div
            initial="hidden"
            animate={isServicesVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              { icon: Icons.Package, title: "Leasingrückläufer", desc: "Professionelle Abwicklung von Leasingrückläufern mit Mengenrabatten und schneller Logistik." },
              { icon: Icons.Recycle, title: "Hardware-Refresh", desc: "Komplettlösungen für IT-Refresh-Projekte mit Ankauf der Altgeräte und Remarketing." },
              { icon: Icons.TrendingUp, title: "Restwertoptimierung", desc: "Maximale Restwerte durch professionelles Remarketing Ihrer ausgedienten IT-Hardware." }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-16 h-16 mb-6 bg-gradient-to-br from-[#1f7db6] to-[#143e6e] rounded-2xl flex items-center justify-center"
                  >
                    <item.icon className="text-white" size={32} />
                  </motion.div>
                  <h3 className="font-bold text-xl mb-3 text-[#143e6e]">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mengen Section */}
      <section ref={refMengen} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isMengenVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Mengen & Konditionen
          </motion.h2>

          <motion.div
            initial="hidden"
            animate={isMengenVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {[
              { count: "50+", title: "Geräte", desc: "Attraktive Konditionen", color: "from-blue-500 to-blue-600" },
              { count: "100+", title: "Geräte", desc: "Premium-Konditionen", color: "from-[#1f7db6] to-[#143e6e]" },
              { count: "500+", title: "Geräte", desc: "VIP-Konditionen", color: "from-purple-500 to-purple-600" }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 text-center">
                  <div className={`text-6xl font-bold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-4`}>
                    {item.count}
                  </div>
                  <h3 className="font-bold text-2xl mb-2 text-[#143e6e]">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 variants={slideUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Bereit für professionelles IT-Remarketing?
          </motion.h2>
          <motion.p variants={slideUp} className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Fordern Sie jetzt ein unverbindliches Angebot an
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/request-quote">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Angebot anfordern
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};
